/**
 * Descobre se a FONTE DO SISTEMA consegue desenhar um emoji.
 *
 * Por que isso existe: o Windows (Segoe UI Emoji) nao desenha boa parte das
 * sequencias novas do Unicode. O emoji-mart no modo "native" manda o caractere
 * direto para a fonte, entao esses aparecem quebrados de dois jeitos:
 *
 *   1. quadradinho vazio (tofu) -- caractere que a fonte nao tem;
 *   2. sequencia ZWJ que nao se une, ex.: 👨‍👩‍👧 vira 👨👩👧 (tres bonecos
 *      colados). Esse e o caso da maioria dos "bugados" na aba Pessoas.
 *
 * Cada caso precisa de um teste diferente:
 *   - tofu: comparamos os pixels com um caractere que com certeza nao existe.
 *   - ZWJ: medimos a largura. Se uniu, ocupa a largura de UM emoji; se nao
 *     uniu, ocupa a largura de varios.
 *
 * O resultado fica em cache: o picker pergunta por centenas de emojis.
 */

const TAMANHO = 32;
const FONTE = `${TAMANHO}px "Segoe UI Emoji", "Apple Color Emoji", "Noto Color Emoji", sans-serif`;

// caractere reservado -- nenhuma fonte desenha, sempre vira tofu
const INEXISTENTE = "￿";
const REFERENCIA = "\u{1F600}"; // 😀, presente em qualquer fonte de emoji

const cache = new Map();

let ctx = null;
let pixelsTofu = null;
let larguraDeUm = 0;

const iniciar = () => {
  if (ctx !== null) return ctx;

  try {
    const canvas = document.createElement("canvas");
    canvas.width = TAMANHO * 4;
    canvas.height = TAMANHO * 1.5;

    const c = canvas.getContext("2d", { willReadFrequently: true });
    if (!c) {
      ctx = false;
      return ctx;
    }

    c.font = FONTE;
    c.textBaseline = "top";

    ctx = c;
    pixelsTofu = desenhar(INEXISTENTE);
    larguraDeUm = c.measureText(REFERENCIA).width;

    // se nem o 😀 mede algo coerente, o ambiente nao e confiavel:
    // desliga o filtro em vez de esconder tudo
    if (!larguraDeUm) ctx = false;
  } catch (e) {
    ctx = false;
  }

  return ctx;
};

const desenhar = texto => {
  ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
  ctx.fillText(texto, 0, 0);
  return ctx
    .getImageData(0, 0, ctx.canvas.width, ctx.canvas.height)
    .data.join(",");
};

/**
 * @param {string} emoji caractere(s) do emoji
 * @returns {boolean} true quando da para exibir
 */
export const emojiSuportado = emoji => {
  const texto = String(emoji || "");
  if (!texto) return false;

  if (cache.has(texto)) return cache.get(texto);

  const c = iniciar();
  if (c === false) return true; // sem canvas: nao filtra nada

  let ok = true;

  try {
    // 1) sequencia ZWJ: precisa virar UM desenho so
    if (texto.includes("‍")) {
      const largura = c.measureText(texto).width;
      // 1.4x da margem para variacoes de metrica sem aceitar dois emojis
      ok = largura <= larguraDeUm * 1.4;
    } else {
      // 2) caractere solto: nao pode sair igual ao tofu
      ok = desenhar(texto) !== pixelsTofu;
    }
  } catch (e) {
    ok = true;
  }

  cache.set(texto, ok);
  return ok;
};

/**
 * Filtro no formato que o emoji-mart espera (`emojisToShowFilter`).
 * Recebe o objeto do emoji e devolve se ele entra na lista.
 */
export const filtroDeEmoji = dados => {
  if (!dados) return true;

  // O emoji-mart entrega o item cru do dataset, que traz `unified`
  // ("1f469-200d-1f4bb") e nao o caractere. Se vier `native`, ja e o caractere.
  const nativo =
    dados.native || (dados.skins && dados.skins[0] && dados.skins[0].native);

  if (nativo) return emojiSuportado(nativo);

  const unified = dados.unified;
  if (typeof unified !== "string" || !unified) return true;

  let texto;
  try {
    texto = unified
      .split("-")
      .map(p => String.fromCodePoint(parseInt(p, 16)))
      .join("");
  } catch (e) {
    return true; // codepoint estranho: melhor mostrar do que sumir
  }

  return emojiSuportado(texto);
};
